import { useEffect, useMemo, useState } from 'react'
import { Box, Typography } from '@mui/material'
import TouchAppOutlinedIcon from '@mui/icons-material/TouchAppOutlined'
import type { PrototypeResults, PrototypeScreen } from '@/services/prototypeService'
import GazeDot from './GazeDot'
import GazeHeatmap from './GazeHeatmap'

/** What the stage needs to play one respondent's prototype session (Z-98). */
export interface PrototypeReplayData {
  screens: Record<string, Pick<PrototypeScreen, 'node_id' | 'name' | 'width' | 'height' | 'image_url'>>
  startNodeId: string
  /** Null in aggregate mode: the stage then just shows the first screen. */
  session: NonNullable<PrototypeResults['individual']> | null
}

/** A click stays on screen this long after it happened, fading out. */
const CLICK_TRAIL_MS = 1600
/** Longest gap between gaze samples the dot bridges (a blink); past it, hidden. */
const GAZE_MAX_GAP_MS = 300
/** The heat map follows the playhead in steps this long, not every frame. */
const HEAT_STEP_MS = 250

type ReplayGaze = NonNullable<NonNullable<PrototypeReplayData['session']>['gaze']>

/** Gaze on `nodeId` at `ms`, interpolated between the samples around it. */
function gazeAt(gaze: ReplayGaze, nodeId: string, ms: number): { x: number; y: number } | null {
  let lo = 0
  let hi = gaze.length
  while (lo < hi) {
    const mid = (lo + hi) >> 1
    if (gaze[mid].t_ms <= ms) lo = mid + 1
    else hi = mid
  }
  const left = gaze[lo - 1]
  const right = gaze[lo]
  const near = (g: ReplayGaze[number] | undefined) => !!g && g.node_id === nodeId && Math.abs(g.t_ms - ms) <= GAZE_MAX_GAP_MS
  if (near(left) && near(right) && right!.t_ms > left!.t_ms) {
    const f = (ms - left!.t_ms) / (right!.t_ms - left!.t_ms)
    return { x: left!.x + (right!.x - left!.x) * f, y: left!.y + (right!.y - left!.y) * f }
  }
  if (near(left)) return { x: left!.x, y: left!.y }
  if (near(right)) return { x: right!.x, y: right!.y }
  return null
}

/**
 * Rebuilt replay of a prototype session: the screen that was on show at the
 * playhead, with the clicks that just happened on it (red = missed every
 * hotspot). Rebuilt from events, not filmed — no transitions, hovers, typed
 * text or scrolling (decision 2026-09-23). With eye tracking, a heat map of
 * where they looked during this visit to the screen builds up to the playhead,
 * with the gaze dot on top.
 */
export default function PrototypeReplay({ replay, currentMs }: { replay: PrototypeReplayData; currentMs: number }) {
  // The whole frame must fit the stage. A percentage height + aspect-ratio box
  // inside the stage's grid has no definite height to resolve against, so it
  // grew to the image's width and the stage clipped a tall phone frame to its
  // top third — measure the stage and fit the frame in pixels instead.
  const [stageEl, setStageEl] = useState<HTMLDivElement | null>(null)
  const [stageSize, setStageSize] = useState({ w: 0, h: 0 })
  useEffect(() => {
    if (!stageEl) return
    const ro = new ResizeObserver(([entry]) => {
      const { width, height } = entry.contentRect
      setStageSize({ w: width, h: height })
    })
    ro.observe(stageEl)
    return () => ro.disconnect()
  }, [stageEl])

  const stays = replay.session?.screens ?? []
  let nodeId = stays[0]?.node_id ?? replay.startNodeId
  let stayStart = 0
  for (const s of stays) {
    if (s.start_ms <= currentMs) {
      nodeId = s.node_id
      stayStart = s.start_ms
    } else break
  }
  const gaze = replay.session?.gaze ?? null
  const heatStep = Math.floor(currentMs / HEAT_STEP_MS)
  const heatPoints = useMemo(() => {
    if (!gaze) return []
    const until = (heatStep + 1) * HEAT_STEP_MS
    return gaze.filter((g) => g.node_id === nodeId && g.t_ms >= stayStart && g.t_ms <= until)
  }, [gaze, nodeId, stayStart, heatStep])
  const gazePoint = gaze ? gazeAt(gaze, nodeId, currentMs) : null
  const screen = replay.screens[nodeId]
  const clicks = (replay.session?.clicks ?? []).filter(
    (c) => c.node_id === nodeId && c.t_ms <= currentMs && currentMs - c.t_ms < CLICK_TRAIL_MS && c.x != null && c.y != null,
  )

  if (!screen?.image_url) {
    return (
      <Box sx={{ textAlign: 'center', color: 'rgba(255,255,255,.7)', px: 3 }}>
        <TouchAppOutlinedIcon sx={{ fontSize: 40, mb: 1 }} />
        <Typography sx={{ fontWeight: 700 }}>{screen?.name ?? nodeId}</Typography>
      </Box>
    )
  }

  const ratio = (screen.width || 16) / (screen.height || 9)
  const fitW = Math.min(stageSize.w, stageSize.h * ratio)
  const fitH = fitW / ratio

  return (
    <Box ref={setStageEl} sx={{ position: 'absolute', inset: 0, display: 'grid', placeItems: 'center' }}>
      <Box sx={{ position: 'relative', width: fitW, height: fitH, visibility: fitW > 0 ? 'visible' : 'hidden' }}>
        <Box component="img" src={screen.image_url} alt={screen.name} sx={{ width: '100%', height: '100%', objectFit: 'contain', display: 'block' }} />
        {gaze && <GazeHeatmap points={heatPoints} opacity={0.6} />}
        {gazePoint && <GazeDot x={gazePoint.x} y={gazePoint.y} />}
        {clicks.map((c) => {
          const age = (currentMs - c.t_ms) / CLICK_TRAIL_MS
          return (
            <Box
              key={`${c.t_ms}-${c.x}-${c.y}`}
              sx={{
                position: 'absolute',
                left: `${c.x! * 100}%`,
                top: `${c.y! * 100}%`,
                width: 26,
                height: 26,
                ml: '-13px',
                mt: '-13px',
                borderRadius: '50%',
                border: '3px solid #fff',
                bgcolor: c.handled ? 'rgba(95,179,168,.75)' : 'rgba(234,80,72,.8)',
                opacity: 1 - age,
                transform: `scale(${1 + age * 0.6})`,
                pointerEvents: 'none',
              }}
            />
          )
        })}
      </Box>
      <Box
        sx={{
          position: 'absolute',
          left: 10,
          bottom: 10,
          px: 1,
          py: 0.25,
          borderRadius: 1,
          bgcolor: 'rgba(0,0,0,.55)',
          color: '#fff',
          fontSize: 12,
          fontWeight: 600,
        }}
      >
        {screen.name}
      </Box>
    </Box>
  )
}
